<template>
    <section class="text-setting__section">
        <div class="text-setting__content">
            <header class="text-setting__header">
                <h2
                    class="text-setting__title text-setting__title--editable"
                    contenteditable
                    @blur="onTitleInput"
                    v-text="preset.name"
                />
            </header>

            <settings-responsive-options @input="onOptionInput" />

            <ul class="text-setting__list">
                <li class="text-setting__item">
                    <control-text-color
                        :value="presetStyles.color"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-background-color
                        v-if="type !== 'link'"
                        :value="presetStyles.backgroundColor"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-font-family
                        v-if="!state"
                        :value="presetStyles.fontFamily"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-font-size
                        v-if="!state"
                        :value="presetStyles.fontSize"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-font-weight
                        v-if="!state"
                        :value="presetStyles.fontWeight"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-line-height
                        v-if="!state"
                        :value="presetStyles.lineHeight"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-text-decoration
                        v-if="type == 'link'"
                        :value="presetStyles.textDecoration"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-box-shadow
                        v-if="type === 'button'"
                        :value="presetStyles.boxShadow"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-padding
                        v-if="type === 'button' && !state"
                        :value="presetStyles.paddingLeft"
                        side="x"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-padding
                        v-if="type === 'button' && !state"
                        :value="presetStyles.paddingTop"
                        side="y"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-border-radius
                        v-if="type === 'button' && !state"
                        :value="presetStyles.borderRadius"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-border-width
                        v-if="type === 'button' && !state"
                        :value="presetStyles.borderWidth"
                        @input="onInput"
                    />
                </li>

                <li class="text-setting__item">
                    <control-border-color
                        v-if="type === 'button'"
                        :value="presetStyles.borderColor"
                        @input="onInput"
                    />
                </li>
            </ul>
        </div>

        <figure class="text-setting__preview" :class="deviceClass">
            <div class="text-setting__preview-container" :class="previewClass">
                <dynamic-element
                    :class="presetNew.classes"
                    :tag="previewTag"
                    contenteditable
                    @input="onPreviewTextInput"
                    v-text="previewTextNew"
                />
            </div>
        </figure>
    </section>
</template>

<script>
import ControlBackgroundColor from '@/components/settings/shared/controls/control-background-color';
import ControlBorderColor from '@/components/settings/shared/controls/control-border-color';
import ControlBorderRadius from '@/components/settings/shared/controls/control-border-radius';
import ControlBorderWidth from '@/components/settings/shared/controls/control-border-width';
import ControlBoxShadow from '@/components/settings/shared/controls/control-box-shadow';
import ControlFontFamily from '@/components/settings/shared/controls/control-font-family';
import ControlFontSize from '@/components/settings/shared/controls/control-font-size';
import ControlFontWeight from '@/components/settings/shared/controls/control-font-weight';
import ControlLineHeight from '@/components/settings/shared/controls/control-line-height';
import ControlPadding from '@/components/settings/shared/controls/control-padding';
import ControlTextColor from '@/components/settings/shared/controls/control-text-color';
import ControlTextDecoration from '@/components/settings/shared/controls/control-text-decoration';

import SettingsResponsiveOptions from '@/components/settings/shared/settings-responsive-options';
import DynamicElement from '@/components/utilities/dynamic-element';
import mapFilters from '@/utils/map-filters';
import baseOption from '@/options/mixins/base-option';
import { generateClass, getClassDefinition, getCssProperties } from '@/utils/tailwind-helper';
import { STATE_CLASSES } from '@/data/constants/classes';

export default {
    name: 'settings-preset',

    components: {
        DynamicElement,
        ControlBackgroundColor,
        ControlBorderColor,
        ControlBorderRadius,
        ControlBorderWidth,
        ControlBoxShadow,
        ControlFontFamily,
        ControlFontSize,
        ControlFontWeight,
        ControlLineHeight,
        ControlPadding,
        ControlTextColor,
        ControlTextDecoration,
        SettingsResponsiveOptions,
    },

    mixins: [baseOption],

    props: {
        preset: {
            type: Object,
            required: true,
        },

        previewText: {
            type: String,
            required: true,
        },

        type: {
            type: String,
            default: STATE_CLASSES.HEADING,
        },
    },

    data() {
        return {
            isUpdatingPreview: false,
            presetNew: this.preset,
            previewTextNew: this.previewText,

            deviceClass: STATE_CLASSES.DESKTOP,
            selectedScreen: 'all',
            state: '',
        };
    },

    computed: {
        previewClass() {
            switch (this.type) {
                case 'heading':
                    return STATE_CLASSES.HEADING;

                case 'paragraph':
                    return STATE_CLASSES.PARAGRAPH;

                case 'button':
                    return STATE_CLASSES.BUTTON;

                default:
                    return '';
            }
        },

        previewTag() {
            switch (this.type) {
                case 'heading':
                    return 'h3';

                case 'paragraph':
                    return 'p';

                case 'button':
                    return 'button';

                default:
                    return 'div';
            }
        },

        presetClasses() {
            return this.presetNew.classes.split(' ');
        },

        // presets are stored as classes, but the controls expect a CSS value
        presetStyles() {
            const presetStylesNew = {};

            this.presetClasses.forEach((className) => {
                const {
                    tailwindDefinitionProp,
                    screen,
                    side,
                    state,
                    value,
                } = getClassDefinition(className);

                const baseCssProp = tailwindDefinitionProp === 'textColor' ? 'color' : tailwindDefinitionProp;
                const cssProps = getCssProperties(baseCssProp, side);

                cssProps.forEach((cssProp) => {
                    if (cssProp === 'text-decoration-line') cssProp = 'text-decoration';

                    cssProp = this.camelCase(cssProp);

                    const matchingScreen = screen === this.selectedScreen;
                    const matchingState = state === this.state;
                    const defaultClass = screen === 'all' && !presetStylesNew[cssProp];

                    if ((matchingScreen && matchingState) || defaultClass) {
                        presetStylesNew[cssProp] = value;
                    }
                });
            });

            return presetStylesNew;
        },
    },

    watch: {
        previewText(previewText) {
            // we don't want to update the component the user is typing into
            // otherwise this forces a re-render which moves the cursor to the beginning of the input text after each keystroke
            if (this.isUpdatingPreview) {
                this.isUpdatingPreview = false;
                return;
            }

            this.previewTextNew = previewText;
        },
    },

    methods: {
        ...mapFilters(['camelCase']),

        onInput(inputData) {
            const presetClassesNew = [...this.presetClasses];

            const {
                tailwindDefinitionProp: tailwindDefinitionPropNew,
                value: valueNew,
                side: sideNew = '',
            } = inputData;

            const existingClassIndex = presetClassesNew.findIndex((className) => {
                const {
                    tailwindDefinitionProp,
                    screen,
                    side = '',
                    state = '',
                } = getClassDefinition(className);

                const matchingGroup = tailwindDefinitionProp === tailwindDefinitionPropNew;
                const matchingSide = side === sideNew;
                const matchingScreen = screen === this.selectedScreen;
                const matchingState = state === this.state;

                return matchingGroup && matchingSide && matchingScreen && matchingState;
            });

            if (valueNew) {
                const className = generateClass({
                    value: valueNew,
                    side: sideNew,
                    screen: this.selectedScreen,
                    state: this.state,
                    tailwindDefinitionProp: tailwindDefinitionPropNew,
                });

                if (existingClassIndex > -1) {
                    presetClassesNew.splice(existingClassIndex, 1, className);
                } else {
                    presetClassesNew.push(className);
                }
            } else {
                presetClassesNew.splice(existingClassIndex, 1);
            }

            this.presetNew.classes = presetClassesNew.join(' ');
            this.update();
        },

        onTitleInput(e) {
            this.presetNew.name = e.target.innerText;
            this.update();
        },

        onOptionInput({ device, state }) {
            this.deviceClass = device.deviceClass;
            this.selectedScreen = device.name;
            this.state = state;
        },

        onPreviewTextInput(e) {
            const { innerText } = e.target;

            this.isUpdatingPreview = true;

            // notify other preview boxes of the change, so they can be kept in sync
            this.$emit('preview-input', innerText);
        },

        update() {
            this.$emit('input', this.presetNew);
        },
    },
};
</script>
